/**
 * Browser-side orchestration for the client-compiled QB import (issue #212):
 * fetch the resolution maps (small tables the server already holds), run the
 * pure compiler, serialize NDJSON parts, upload them to R2 via the existing
 * /qb-bundle/upload endpoint, and launch the workflow in compiled mode.
 *
 * Kept separate from utils/qb-compile (pure) — this module owns the network.
 */

import { accountingService } from '@/services/accounting-service';
import { apiService } from '@/services/api-service';
import type { Account, Person } from '@/types/models';
import { accountDisplayName } from '@/utils/account-display';
import { type ParsedEntry } from '@/utils/journal-csv-parser';
import {
	buildGlByCode,
	compileQbBundle,
	type QbCompiledResult,
	type QbCompileDetails,
	type QbCompileGlCode,
	type QbCompileResolutions,
	serializeCompiledBundle,
	type UnresolvedOwner,
	type UnresolvedVendor,
} from '@/utils/qb-compile';
import { resolveAccountForCustomerName } from '@/utils/qb-customer-match';

export interface QbCompileClientResolutions extends QbCompileResolutions {
	/** Balance-sheet clearing GL used by auto-correction; null when none found. */
	clearingGl: QbCompileGlCode | null;
	/** Raw vendor rows (global) — fuels the unknown-payee create-or-map prompt. */
	vendorList: Array<{ id: string; name: string }>;
	/** Manual owner overrides (lower(name) → accounts.id) from the unmatched-owner
	 *  review; consulted by `resolveAccountId` before the automatic matcher. */
	ownerNameOverrides: Map<string, string>;
	/** Accounts (incl. IIF-imported) for the unmatched-owner map typeahead. */
	accountList: Array<{ id: string; label: string; accountNumber?: string }>;
}

// The list endpoints cap `limit` at 500 (api/pagination.ts MAX_LIMIT), so a
// single request silently truncates large associations and the compiler would
// then emit unresolved vendors/GLs/banks. Page through to completion.
const PAGE_LIMIT = 500;
async function fetchAllPages<T>(fetchPage: (page: number, limit: number) => Promise<{ data?: T[] }>): Promise<T[]> {
	const all: T[] = [];
	for (let page = 1; page <= 100; page++) {
		const rows = (await fetchPage(page, PAGE_LIMIT)).data ?? [];
		all.push(...rows);
		if (rows.length < PAGE_LIMIT) break;
	}
	return all;
}

/**
 * Build the compiler's resolution maps from data the client can already read.
 * Everything here is association-scoped; each list is paged to completion so the
 * compiler resolves against the same full set the server pipeline would.
 */
export async function buildQbCompileResolutions(associationId: string): Promise<QbCompileClientResolutions> {
	const [glCodeRows, vendorRows, bankRows, funds, association] = await Promise.all([
		fetchAllPages((page, limit) => accountingService.listGlCodes({ limit, page, activeOnly: false })),
		fetchAllPages((page, limit) => accountingService.listVendors({ limit, page, activeOnly: false })),
		fetchAllPages((page, limit) => accountingService.listBankAccounts({ associationId, activeOnly: false, limit, page })),
		accountingService.listFunds(associationId),
		apiService.fetchApi(`/associations/${associationId}`),
	]);

	// Keyed by BOTH code and the original QuickBooks number (external_id), so an
	// incoming QB line referencing the old number resolves to the renumbered
	// standard code — migrating the client onto the standard chart instead of
	// auto-creating a per-association GL row.
	const glByCode = buildGlByCode(glCodeRows.map((gl) => ({
		id: gl.id, code: gl.code, name: gl.name, accountType: gl.accountType, externalId: gl.externalId,
	})));
	// Auto-correction clearing candidate: a balance-sheet (non-P&L, non-AR/AP)
	// account named like a clearing/undeposited bucket.
	let clearingGl: QbCompileGlCode | null = null;
	for (const entry of glByCode.values()) {
		if (/clearing|undeposit|suspense/i.test(entry.name)
			&& !['INC', 'EXP', 'OINC', 'OEXP', 'AR', 'AP'].includes(entry.accountType)) {
			clearingGl = entry;
			break;
		}
	}

	// Accounts + attached people: paginate (server caps limit at 500). Owner
	// resolution goes through the SHARED matchSection convention (external_name
	// exact → account-number prefix → owner-name tie-break) — the same matcher the
	// Transactions and Starting-Balances importers use — so associations that key
	// owners by a plain number ("101 - Stepka") resolve identically to
	// shortcode-style ones ("742PS - Jones"). `accountIdByShortcode` (keyed by
	// account_number) is retained as the pure-compiler fallback.
	const [accountRows, peopleRows] = await Promise.all([
		fetchAllPages<Account>((page, limit) => accountingService.listAccounts({
			associationId, includeArchived: true, limit, page,
		} as Parameters<typeof accountingService.listAccounts>[0]) as Promise<{ data?: Account[] }>),
		fetchAllPages<Person & { accountId?: string }>((page, limit) =>
			accountingService.listPeople({ associationId, limit, page } as Parameters<typeof accountingService.listPeople>[0])),
	]);
	const accountIdByShortcode = new Map<string, string>();
	for (const a of accountRows) {
		if (a.accountNumber && !accountIdByShortcode.has(a.accountNumber)) {
			accountIdByShortcode.set(a.accountNumber, a.id);
		}
	}
	const peopleByAccountId = new Map<string, Person[]>();
	for (const p of peopleRows) {
		if (!p.accountId) continue;
		const list = peopleByAccountId.get(p.accountId) ?? [];
		list.push(p);
		peopleByAccountId.set(p.accountId, list);
	}
	// User overrides from the unmatched-owner review (name→accountId), consulted
	// before the automatic matcher so a manual map always wins.
	const ownerNameOverrides = new Map<string, string>();
	const resolveAccountId = (qbName: string | null | undefined): string | null => {
		if (!qbName) return null;
		const override = ownerNameOverrides.get(qbName.trim().toLowerCase());
		if (override) return override;
		return resolveAccountForCustomerName(qbName, accountRows, peopleByAccountId).matched?.id ?? null;
	};
	// Account picker options for the review modal — display name + number so the
	// user can map an unmatched QB name onto any existing/IIF-imported account.
	const accountList = accountRows.map((a) => ({
		id: a.id,
		label: accountDisplayName(a) || a.accountNumber || a.id.slice(0, 8),
		accountNumber: a.accountNumber ?? undefined,
	}));

	const vendorIdByLowerName = new Map<string, string>();
	const vendorList: Array<{ id: string; name: string }> = [];
	for (const v of vendorRows) {
		if (v.name) {
			vendorIdByLowerName.set(v.name.trim().toLowerCase(), v.id);
			vendorList.push({ id: v.id, name: v.name });
		}
	}

	const bankAccountIdByGlCodeId = new Map<string, string>();
	for (const b of bankRows as Array<{ id: string; glCodeId?: string | null }>) {
		if (b.glCodeId) bankAccountIdByGlCodeId.set(b.glCodeId, b.id);
	}

	const fundRows = (funds as { items?: Array<{ id: string; isDefault?: boolean }> }).items ?? [];
	const defaultFundId = fundRows.find((f) => f.isDefault)?.id
		?? (fundRows.length === 1 ? fundRows[0].id : null);

	const assoc = association as { unassignedIncomeGlCodeId?: string | null; unassignedExpenseGlCodeId?: string | null };

	return {
		glByCode,
		accountIdByShortcode,
		resolveAccountId,
		ownerNameOverrides,
		accountList,
		vendorIdByLowerName,
		bankAccountIdByGlCodeId,
		defaultFundId: defaultFundId ?? null,
		fallbackIncomeGlId: assoc.unassignedIncomeGlCodeId ?? null,
		fallbackExpenseGlId: assoc.unassignedExpenseGlCodeId ?? null,
		clearingGl,
		vendorList,
	};
}

/**
 * Probe compile (no upload): run the pure compiler in collect mode and return
 * the distinct payees referenced by bills/bill-payments that aren't known
 * vendors. The UI prompts the user to create-or-map each one, then re-compiles
 * for real. Cheap — the compiler is in-memory and we discard its rows.
 */
export function probeUnresolvedVendors(
	associationId: string,
	importBatchId: string,
	entries: ParsedEntry[],
	resolutions: QbCompileResolutions,
	details?: QbCompileDetails,
): UnresolvedVendor[] {
	const probe = compileQbBundle(entries, resolutions, {
		associationId,
		importBatchId,
		markCleared: true,
		now: Date.now(),
		collectUnresolvedVendors: true,
	}, details);
	return probe.unresolvedVendors;
}

/**
 * Probe compile for OWNERS: run the pure compiler in owner-collect mode and
 * return the distinct QB "Name"s on A/R entries that don't resolve to any
 * account. Unlike vendors these can't be created on the fly, so the modal
 * surfaces them as a BLOCKING review ("N transactions across M unrecognized
 * owners will be journal-only") the user must acknowledge — never silently
 * dropped under a success banner (the West Harbor incident). Cheap: in-memory,
 * rows discarded.
 */
export function probeUnresolvedOwners(
	associationId: string,
	importBatchId: string,
	entries: ParsedEntry[],
	resolutions: QbCompileResolutions,
	details?: QbCompileDetails,
): UnresolvedOwner[] {
	const probe = compileQbBundle(entries, resolutions, {
		associationId,
		importBatchId,
		markCleared: true,
		now: Date.now(),
		collectUnresolvedOwners: true,
	}, details);
	return probe.unresolvedOwners;
}

/** A user's decision for one unmatched owner name. */
export interface OwnerResolution {
	/** The unmatched QB owner name, verbatim (its lower-case form is the key). */
	name: string;
	/** 'map' → attach this name's entries to `accountId`; 'skip' → journal-only. */
	action: 'map' | 'skip';
	/** Required when action === 'map': the account to resolve this name onto. */
	accountId?: string;
}

/**
 * Fold the user's unmatched-owner decisions into `resolutions.ownerNameOverrides`
 * (lower(name) → accounts.id), which `resolveAccountId` consults before the
 * automatic matcher — so a manual map always wins on the real compile. 'skip'
 * rows are left unmapped (they post journal-only). Mutates the passed-in map.
 */
export function applyOwnerResolutions(
	resolutions: Pick<QbCompileClientResolutions, 'ownerNameOverrides'>,
	decisions: OwnerResolution[],
): void {
	for (const d of decisions) {
		if (d.action === 'map' && d.accountId) {
			resolutions.ownerNameOverrides.set(d.name.trim().toLowerCase(), d.accountId);
		}
	}
}

/** A user's create-or-map decision for one unknown payee. */
export interface VendorResolution {
	/** The unknown payee name, verbatim (its lower-case form is the map key). */
	name: string;
	/** 'create' → make a new vendor named `name`; 'map' → reuse `vendorId`. */
	action: 'create' | 'map';
	/** Required when action === 'map': the existing vendor to resolve to. */
	vendorId?: string;
}

/**
 * Resolve the user's create-or-map decisions and fold the resulting name→id
 * pairs into `resolutions.vendorIdByLowerName`, so the real compile resolves
 * every payee. Mutates the passed-in map (it's the compiler's input).
 *
 * IMPLEMENT ME — see the request in chat. Constraints/notes:
 *  - `accountingService.createVendor(body)` POSTs one vendor and RETURNS the
 *    created row (with its `id`). `bulkUpsertVendors` returns only counts (no
 *    ids), so it can't give you the id to map — prefer createVendor here.
 *  - Vendors are GLOBAL + UNIQUE on name. A name you "create" might already
 *    exist from a prior import; decide how to handle a create that collides.
 *  - Map key is `name.trim().toLowerCase()` (matches the compiler's vendorFor).
 */
export async function applyVendorResolutions(
	resolutions: QbCompileClientResolutions,
	decisions: VendorResolution[],
): Promise<void> {
	const map = resolutions.vendorIdByLowerName;

	// A 'create' whose name already exists (vendors are global + UNIQUE on name)
	// resolves onto the existing vendor instead of erroring — keeps re-runs
	// idempotent. listVendors(q) is a substring search, so confirm an exact
	// case-insensitive name match before trusting it.
	const findExistingByName = async (name: string): Promise<string | null> => {
		const target = name.trim().toLowerCase();
		const resp = await accountingService.listVendors({ q: name, limit: 50, activeOnly: false });
		return (resp.data ?? []).find((v) => (v.name ?? '').trim().toLowerCase() === target)?.id ?? null;
	};

	for (const d of decisions) {
		const key = d.name.trim().toLowerCase();
		if (d.action === 'map') {
			if (d.vendorId) map.set(key, d.vendorId);
			continue;
		}
		let id: string | null;
		try {
			id = (await accountingService.createVendor({ name: d.name.trim() })).id;
		} catch (err) {
			// Unique-name collision (or any create failure): fall back to the
			// existing same-name vendor; rethrow if there genuinely isn't one.
			id = await findExistingByName(d.name);
			if (!id) throw err;
		}
		map.set(key, id);
		resolutions.vendorList.push({ id, name: d.name.trim() });
	}
}

export interface CompileAndUploadArgs {
	associationId: string;
	importBatchId: string;
	entries: ParsedEntry[];
	details?: QbCompileDetails;
	resolutions: QbCompileClientResolutions;
	markCleared: boolean;
	reset: boolean;
	allowGaps: boolean;
	onProgress?: (stage: 'compiling' | 'uploading' | 'starting', done: number, total: number) => void;
}

export interface CompileAndUploadResult {
	compiled: QbCompiledResult;
	parts: number;
	workflowId: string;
}

/**
 * Compile → serialize → upload parts → /start (compiled mode). Each part is a
 * small bounded POST (the house bulk pattern); the workflow does the rest.
 */
export async function compileUploadAndStart(args: CompileAndUploadArgs): Promise<CompileAndUploadResult> {
	const { associationId, importBatchId } = args;
	args.onProgress?.('compiling', 0, 1);
	const compiled = compileQbBundle(args.entries, args.resolutions, {
		associationId,
		importBatchId,
		markCleared: args.markCleared,
		now: Date.now(),
	}, args.details);

	const parts = serializeCompiledBundle(compiled);
	const session = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto?.randomUUID?.()
		?? `s-${Date.now().toString(36)}`;
	const compiledParts: string[] = [];
	for (let i = 0; i < parts.length; i++) {
		args.onProgress?.('uploading', i, parts.length);
		const filename = `compiled-part-${String(i).padStart(4, '0')}.ndjson`;
		const resp = await apiService.fetchApi(
			`/accounting/qb-bundle/upload?associationId=${encodeURIComponent(associationId)}&filename=${encodeURIComponent(filename)}&session=${session}`,
			{ method: 'POST', headers: { 'Content-Type': 'application/x-ndjson' }, body: parts[i], timeoutMs: 120_000 } as Parameters<typeof apiService.fetchApi>[1],
		) as { r2Key: string };
		compiledParts.push(resp.r2Key);
	}

	args.onProgress?.('starting', parts.length, parts.length);
	const started = await apiService.fetchApi('/accounting/qb-bundle/start', {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify({
			associationId,
			importBatchId,
			files: [],
			compiledParts,
			markCleared: args.markCleared,
			reset: args.reset,
			allowGaps: args.allowGaps,
		}),
		timeoutMs: 60_000,
	} as Parameters<typeof apiService.fetchApi>[1]) as { workflowId: string };

	return { compiled, parts: parts.length, workflowId: started.workflowId };
}
